/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Directive, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import {
  DND_GRID_ENGINE,
  DndDragSource,
  DndDropTarget,
  DndManager,
  GridDndDropTarget,
} from '@gh/core-ui';
import { concatWith$, map$ } from '@gh/rx/operators';
import { DragSourceType } from 'ag-grid-community';
import { TreeDraggedElement } from '@ali-hm/angular-tree-component';
import { constant, isNil, noop, values } from 'lodash';
import { Subscription } from 'rxjs';

/**
 * This directive makes tree component suitable for <code>DropTarget</code> role in heteragenous DnD operations.
 */
@Directive({
  standalone: false,
  selector: '[ghTreeDropTarget]',
})
export class TreeDropTargetDirective implements OnInit, OnDestroy {
  @Input('ghTreeDropTarget') selector: string;

  private targets: Hash<DndDropTarget> = {};

  private dragSourceSubscription: Subscription;

  private supportedTreeEngines = {
    [DND_GRID_ENGINE]: this.createGridDndDropTarget.bind(this),
  };

  constructor(private elementRef: ElementRef,
              private dndManager: DndManager,
              private treeDraggedElement: TreeDraggedElement) {

  }

  ngOnInit(): void {
    const dndSubscription = this.dndManager.listenDragSources(this.selector);

    this.dragSourceSubscription = dndSubscription.sources.pipe(
      concatWith$(dndSubscription.sourceAdded.pipe(map$((source) => [source]))))
      .subscribe((sources) => sources.forEach((source) => this.ensureDropTargetFor(source)));
  }

  ngOnDestroy(): void {
    values(this.targets).forEach((target) => this.dndManager.removeDropTarget(this.selector, target));

    this.dragSourceSubscription.unsubscribe();
  }

  /**
   * Ensures that drop target is created for specific kind of DnD communication.
   */
  private ensureDropTargetFor(source: DndDragSource): void {
    const target = this.targets[source.engine];
    if (isNil(target)) {
      this.createDropTargetFor(source);
    }
  }

  /**
   * Creates drop target for specific kind of DnD communication.
   */
  private createDropTargetFor(source: DndDragSource): void {
    const dropTargetFactory = this.supportedTreeEngines[source.engine];

    if (isNil(dropTargetFactory)) {
      throw new Error(`DnD engine '${source.engine}' is not supported by tree component`);
    }

    const target = dropTargetFactory(this.elementRef.nativeElement);
    this.targets[source.engine] = target;
    this.dndManager.addDropTarget(this.selector, target);
  }

  /**
   * Create <code>DropTarget</code> suitable for ag-grid component
   */
  private createGridDndDropTarget(containerEl: HTMLElement): GridDndDropTarget {
    return {
      engine: DND_GRID_ENGINE,
      onDragStart: ({ element }) => this.treeDraggedElement.set(element),
      onDragStop: () => {
        queueMicrotask(() => this.treeDraggedElement.set(void 0));
      },
      gridDropTarget: {
        getContainer: constant(containerEl),
        isInterestedIn: (type) => type === DragSourceType.RowDrag,
        getIconName: constant('move'),
        onDragEnter: noop,
        onDragging: noop,
        onDragLeave: noop,
        onDragStop: noop,
      },
    };
  }

}
